import { IconClose16 } from '@fabric-ds/icons/react';
import React, { useEffect, useRef, useState } from 'react';
import FocusLock from 'react-focus-lock';
import PropTypes from 'prop-types';
import { useDataContext } from 'common/lib/helpers/DataContext';
import { trackClickOnVideoViewingUrl, trackClickOnVirtualViewingUrl } from 'common/lib/helpers/trackingService';
import { externalMediaUrlGenerator } from './videoAndVirtualUrlUtils';
import { VideoViewingIcon, VirtualViewingIcon } from './icons';

const Modal = ({ url, closeCallback }) => {
    const iframe = useRef(null);
    useEffect(() => {
        // need to scroll to top or else safari renders the modal on the top while the page is scrolled down
        window.scrollTo(0, 0);
        const escapeListener = event => {
            if (event.code === 'Escape') {
                closeCallback();
            }
        };
        document.addEventListener('keydown', escapeListener);
        iframe.current &&
            iframe.current.contentWindow &&
            iframe.current.contentWindow.document.addEventListener('keydown', escapeListener);
        return () => {
            document.removeEventListener('keydown', escapeListener);
            iframe.current &&
                iframe.current.contentWindow &&
                iframe.current.contentWindow.document.removeEventListener('keydown', escapeListener);
        };
    }, [iframe]);

    return (
        <FocusLock>
            <div
                className="absolute top-0 bottom-0 left-0 right-0 w-full h-screen z-40"
                style={{ background: 'rgba(0,0,0,.9)' }}
                data-testid="360-modal"
            >
                <button
                    className="absolute top-20 right-20 text-white p-16"
                    style={{ background: '#000', opacity: '.44' }}
                    onClick={closeCallback}
                    type="button"
                >
                    <IconClose16 />
                </button>
                <div className="flex flex-col h-full">
                    <iframe
                        ref={iframe}
                        src={externalMediaUrlGenerator(url).toString()}
                        title="Virtuell visning"
                        allowFullScreen
                        allow="xr-spatial-tracking"
                        style={{ height: '100%', width: '100%' }}
                    />
                </div>
            </div>
        </FocusLock>
    );
};

Modal.propTypes = {
    url: PropTypes.string.isRequired,
    closeCallback: PropTypes.func.isRequired,
};

const ViewingButton = ({ url, icon, text, tracking }) => {
    const [open, setOpen] = useState(false);

    useEffect(() => {
        ['overflow-hidden', 'relative', 'h-full'].map(e => document.body.classList.toggle(e, open));
    }, [open]);

    return url ? (
        <>
            <button
                type="button"
                onClick={() => {
                    setOpen(true);
                    tracking();
                }}
                className="flex items-center text-blue-600 font-bold button button--flat"
                aria-label={text}
                data-preview-dont-disable="true"
            >
                {icon}
                <span className="hidden md:block pl-4">{text}</span>
            </button>
            {open ? <Modal url={url} closeCallback={() => setOpen(false)} /> : null}
        </>
    ) : null;
};

ViewingButton.propTypes = {
    url: PropTypes.string.isRequired,
    icon: PropTypes.object,
    text: PropTypes.string.isRequired,
    tracking: PropTypes.func.isRequired,
};

export const VideoAndVirtualViewingButtons = () => {
    const { videoUrl, virtualViewingUrl } = useDataContext();

    const videoButton = (
        <ViewingButton url={videoUrl} text="Video" icon={<VideoViewingIcon />} tracking={trackClickOnVideoViewingUrl} />
    );

    const virtualViewingButton = (
        <ViewingButton
            url={virtualViewingUrl}
            text="360° Visning"
            icon={<VirtualViewingIcon />}
            tracking={trackClickOnVirtualViewingUrl}
        />
    );
    return (
        <>
            {virtualViewingUrl && virtualViewingButton}
            {videoUrl && videoButton}
        </>
    );
};
